import { useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { Search, ChevronLeft, ChevronRight, History, Loader2, SlidersHorizontal, UserPlus, Undo2, Pencil, Ban, MapPin } from 'lucide-react';

import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '@/components/ui/table';
import {
  listarInventario,
  type EstadoItem,
  type OrigenDatos,
  type InventarioItem,
} from '@/services/api/inventario.api';
import { cn } from '@/lib/utils';
import { CONDICION_META } from '@/lib/condicion';
import { useAuthStore } from '@/store/authStore';
import { ResumenActivoDialog } from './ResumenActivoDialog';

const PAGE_SIZE = 20;

// ── Status metadata — label + badge colors + row left-bar accent ──────────
const ESTADO_META: Record<EstadoItem, { label: string; badge: string; bar: string }> = {
  EN_BODEGA:     { label: 'En bodega',  badge: 'bg-emerald-50 text-emerald-700 ring-emerald-200', bar: 'bg-emerald-400' },
  ASIGNADO:      { label: 'Asignado',   badge: 'bg-primary-50 text-primary-700 ring-primary-200', bar: 'bg-primary-500' },
  EN_REPARACION: { label: 'Reparación', badge: 'bg-amber-50 text-amber-800 ring-amber-200',       bar: 'bg-amber-400' },
  DADO_DE_BAJA:  { label: 'De baja',    badge: 'bg-slate-100 text-slate-600 ring-slate-200',      bar: 'bg-slate-300' },
};

// ── Deterministic color avatar for asset type ─────────────────────────────
const AVATAR_PALETTE = [
  'bg-blue-100 text-blue-700',
  'bg-emerald-100 text-emerald-700',
  'bg-violet-100 text-violet-700',
  'bg-amber-100 text-amber-700',
  'bg-rose-100 text-rose-700',
  'bg-cyan-100 text-cyan-700',
  'bg-orange-100 text-orange-700',
  'bg-teal-100 text-teal-700',
];

function AssetAvatar({ tipo }: { tipo: string | null }) {
  const label = tipo ?? 'Otro';
  const letter = label.charAt(0).toUpperCase();
  const color = AVATAR_PALETTE[letter.charCodeAt(0) % AVATAR_PALETTE.length];
  return (
    <div
      className={cn('h-8 w-8 rounded-lg flex items-center justify-center text-xs font-bold shrink-0', color)}
      title={`Tipo: ${label}`}
    >
      {letter}
    </div>
  );
}

function EstadoBadge({ estado }: { estado: EstadoItem }) {
  const { label, badge } = ESTADO_META[estado];
  return (
    <span className={cn(
      'inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ring-1 whitespace-nowrap',
      badge,
    )}>
      {label}
    </span>
  );
}

function AssigneeCell({ empleado }: { empleado: InventarioItem['empleado_asignado'] }) {
  if (!empleado) return <span className="text-muted-foreground/50 text-sm select-none">—</span>;
  const initials = empleado.nombres
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((n) => n[0])
    .join('')
    .toUpperCase();
  return (
    <div className="flex items-center gap-2 min-w-0">
      <div className="h-6 w-6 rounded-full bg-primary-100 text-primary-700 text-[10px] font-bold flex items-center justify-center shrink-0 ring-1 ring-primary-200">
        {initials}
      </div>
      <span className="truncate text-sm text-foreground">{empleado.nombres}</span>
    </div>
  );
}

// ── Filter pill ───────────────────────────────────────────────────────────
function FilterPill({
  active, onClick, children,
}: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'rounded-full px-3 py-1 text-xs font-medium transition-all duration-150',
        active
          ? 'bg-primary-600 text-white shadow-sm'
          : 'bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground',
      )}
    >
      {children}
    </button>
  );
}

// ── Filter definitions ────────────────────────────────────────────────────
const ESTADO_FILTERS: Array<{ value: EstadoItem | 'TODOS'; label: string }> = [
  { value: 'TODOS',         label: 'Todos'      },
  { value: 'EN_BODEGA',     label: 'En bodega'  },
  { value: 'ASIGNADO',      label: 'Asignados'  },
  { value: 'EN_REPARACION', label: 'Reparación' },
  { value: 'DADO_DE_BAJA',  label: 'De baja'    },
];

const ORIGEN_FILTERS: Array<{ value: OrigenDatos | 'TODOS'; label: string }> = [
  { value: 'TODOS',    label: 'Todos'   },
  { value: 'GLPI',     label: 'GLPI'    },
  { value: 'COMPRAS',  label: 'Compras' },
];

interface Props {
  onVerHistorial: (idItemMaestro: string) => void;
  /** Abre el dialog de asignación. Solo se ofrece en items EN_BODEGA. */
  onAsignar?: (item: InventarioItem) => void;
  /** Abre el dialog de devolución. Solo se ofrece en items ASIGNADO. */
  onDevolver?: (item: InventarioItem) => void;
  /** Abre el dialog de edición de condición. Solo para items COMPRAS. */
  onEditarCondicion?: (item: InventarioItem) => void;
  /** Abre el dialog de dar de baja. No disponible si ya está DADO_DE_BAJA. */
  onDarDeBaja?: (item: InventarioItem) => void;
}

/**
 * Listado navegable del catálogo completo (GLPI + Compras).
 * Diseño con barra lateral de color por estado, avatar de tipo,
 * jerarquía tipográfica serial → detalle, chip de origen y filtros contextuales.
 */
export function InventarioLista({ onVerHistorial, onAsignar, onDevolver, onEditarCondicion, onDarDeBaja }: Props) {
  // COMPRAS solo ve su inventario: el backend fuerza origen=COMPRAS, así que aquí
  // ocultamos el filtro y la columna de origen (siempre sería "Compras").
  const esCompras = useAuthStore((s) => s.user?.rol === 'COMPRAS');
  const colCount = esCompras ? 7 : 8;

  const [busqueda, setBusqueda] = useState('');
  const [busquedaActiva, setBusquedaActiva] = useState('');
  const [estado, setEstado] = useState<EstadoItem | 'TODOS'>('TODOS');
  const [origen, setOrigen] = useState<OrigenDatos | 'TODOS'>('TODOS');
  const [page, setPage] = useState(1);
  /** Activo cuyo resumen está abierto. Usa la fila en memoria: no pide red. */
  const [resumen, setResumen] = useState<InventarioItem | null>(null);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['inventario-items', { busquedaActiva, estado, origen, page }],
    queryFn: () => listarInventario({
      page,
      pageSize: PAGE_SIZE,
      busqueda: busquedaActiva || undefined,
      estado: estado === 'TODOS' ? undefined : estado,
      origen: origen === 'TODOS' ? undefined : origen,
    }),
    placeholderData: keepPreviousData,
  });

  const total     = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const items     = data?.items ?? [];

  function aplicarBusqueda() { setBusquedaActiva(busqueda); setPage(1); }
  function cambiarEstado(v: EstadoItem | 'TODOS') { setEstado(v); setPage(1); }
  function cambiarOrigen(v: OrigenDatos | 'TODOS') { setOrigen(v); setPage(1); }

  return (
    <div className="space-y-3">

      {/* ── Search bar ──────────────────────────────────────────────── */}
      <form
        onSubmit={(e) => { e.preventDefault(); aplicarBusqueda(); }}
        className="flex items-center gap-2"
      >
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input
            placeholder="Buscar por serial, tipo, especificación o colaborador…"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            className="pl-9 bg-background"
            aria-label="Buscar en el inventario"
          />
        </div>
        <Button type="submit" variant="secondary" disabled={isFetching} className="shrink-0 gap-1.5">
          {isFetching
            ? <Loader2 className="h-4 w-4 animate-spin" />
            : <Search className="h-4 w-4" />}
          Buscar
        </Button>
      </form>

      {/* ── Filter bar ──────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-2">
        <SlidersHorizontal className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
        {ESTADO_FILTERS.map((f) => (
          <FilterPill key={f.value} active={estado === f.value} onClick={() => cambiarEstado(f.value)}>
            {f.label}
          </FilterPill>
        ))}
        {!esCompras && (
          <>
            <span className="text-muted-foreground/40 text-xs select-none">·</span>
            {ORIGEN_FILTERS.map((f) => (
              <FilterPill key={f.value} active={origen === f.value} onClick={() => cambiarOrigen(f.value)}>
                {f.label}
              </FilterPill>
            ))}
          </>
        )}
        {total > 0 && (
          <span className="ml-auto text-xs text-muted-foreground tabular-nums">
            {total} activo{total !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      {/* ── Table card ──────────────────────────────────────────────── */}
      <Card className="overflow-hidden p-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/30 hover:bg-muted/30 border-b">
                {/* left-bar column — no header */}
                <TableHead className="w-[4px] p-0 border-0" />
                {/* avatar column — no header */}
                <TableHead className="w-10 px-2" />
                <TableHead className="font-semibold">Activo</TableHead>
                <TableHead className="hidden sm:table-cell font-semibold">Condición</TableHead>
                <TableHead className="font-semibold">Estado</TableHead>
                {!esCompras && <TableHead className="hidden sm:table-cell font-semibold">Origen</TableHead>}
                <TableHead className="hidden lg:table-cell font-semibold">Asignado a</TableHead>
                <TableHead className="text-right font-semibold w-16">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                [...Array(7)].map((_, i) => (
                  <TableRow key={i} className="border-b last:border-0">
                    <TableCell colSpan={colCount} className="py-2.5 px-4">
                      <div className="skeleton h-9 rounded-lg" />
                    </TableCell>
                  </TableRow>
                ))
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={colCount} className="py-20 text-center">
                    <div className="flex flex-col items-center gap-3 text-muted-foreground">
                      <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center">
                        <Search className="h-6 w-6 opacity-40" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground">Sin resultados</p>
                        <p className="text-xs mt-0.5">
                          Ajustá los filtros o cambiá el término de búsqueda.
                        </p>
                      </div>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                items.map((it) => {
                  const em = ESTADO_META[it.estado_actual];
                  const detalle = [
                    it.tipo ? it.tipo.toLowerCase() : null,
                    it.especificacion ?? it.modelo ?? it.nombre_glpi ?? null,
                  ]
                    .filter(Boolean)
                    .join(' · ');

                  return (
                    <TableRow
                      key={it.id_item_maestro}
                      className="group border-b last:border-0 hover:bg-muted/30 transition-colors duration-100"
                    >
                      {/* ── Status accent bar ── */}
                      <TableCell className="p-0 w-[4px]">
                        <div className={cn('w-[4px] h-full min-h-[52px] rounded-r-sm', em.bar)} />
                      </TableCell>

                      {/* ── Avatar ── */}
                      <TableCell className="px-3 py-3 w-10">
                        <AssetAvatar tipo={it.tipo} />
                      </TableCell>

                      {/* ── Serial + detail — abre el resumen del activo ── */}
                      <TableCell className="py-3">
                        <button
                          type="button"
                          onClick={() => setResumen(it)}
                          className="text-left group/serial rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1"
                          title="Ver resumen del activo"
                          aria-label={`Ver resumen de ${it.serial_global}`}
                        >
                          <p className="font-mono text-sm font-semibold text-foreground tracking-tight leading-none group-hover/serial:text-primary-700 group-hover/serial:underline underline-offset-2 transition-colors">
                            {it.serial_global}
                          </p>
                          {detalle && (
                            <p className="text-xs text-muted-foreground mt-1 capitalize leading-snug">
                              {detalle}
                            </p>
                          )}
                          {it.ubicacion && (
                            <p className="text-xs text-muted-foreground/80 mt-0.5 flex items-center gap-1 leading-snug">
                              <MapPin className="h-3 w-3 shrink-0" aria-hidden="true" />
                              <span className="truncate" title={it.ubicacion}>{it.ubicacion}</span>
                            </p>
                          )}
                        </button>
                      </TableCell>

                      {/* ── Condition ── */}
                      <TableCell className="hidden sm:table-cell py-3">
                        {it.condicion ? (() => {
                          const meta = CONDICION_META[it.condicion];
                          const Icon = meta.icon;
                          return (
                            <span className={cn(
                              'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ring-1',
                              meta.badge,
                            )}>
                              <Icon className="h-3 w-3" />
                              {meta.label}
                            </span>
                          );
                        })() : (
                          <span className="text-muted-foreground/40 text-sm select-none">—</span>
                        )}
                      </TableCell>

                      {/* ── Status ── */}
                      <TableCell className="py-3">
                        <EstadoBadge estado={it.estado_actual} />
                      </TableCell>

                      {/* ── Origin chip ── */}
                      {!esCompras && (
                        <TableCell className="hidden sm:table-cell py-3">
                          <span className={cn(
                            'inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ring-1',
                            it.origen_datos === 'GLPI'
                              ? 'bg-violet-50 text-violet-700 ring-violet-200'
                              : 'bg-sky-50 text-sky-700 ring-sky-200',
                          )}>
                            {it.origen_datos}
                          </span>
                        </TableCell>
                      )}

                      {/* ── Assignee ── */}
                      <TableCell className="hidden lg:table-cell py-3 max-w-[200px]">
                        <AssigneeCell empleado={it.empleado_asignado} />
                      </TableCell>

                      {/* ── Actions ── */}
                      <TableCell className="py-3 pr-4 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {onAsignar && it.estado_actual === 'EN_BODEGA' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => onAsignar(it)}
                              title="Asignar a un colaborador"
                              className="h-8 w-8 p-0 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 opacity-40 group-hover:opacity-100 transition-opacity duration-150"
                              aria-label={`Asignar ${it.serial_global}`}
                            >
                              <UserPlus className="h-4 w-4" />
                            </Button>
                          )}
                          {onDarDeBaja && it.estado_actual !== 'DADO_DE_BAJA' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => onDarDeBaja(it)}
                              title="Dar de baja"
                              className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50 opacity-40 group-hover:opacity-100 transition-opacity duration-150"
                              aria-label={`Dar de baja ${it.serial_global}`}
                            >
                              <Ban className="h-4 w-4" />
                            </Button>
                          )}
                          {onEditarCondicion && it.origen_datos === 'COMPRAS' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => onEditarCondicion(it)}
                              title="Editar condición"
                              className="h-8 w-8 p-0 text-slate-500 hover:text-slate-700 hover:bg-slate-100 opacity-40 group-hover:opacity-100 transition-opacity duration-150"
                              aria-label={`Editar condición de ${it.serial_global}`}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                          )}
                          {onDevolver && it.estado_actual === 'ASIGNADO' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => onDevolver(it)}
                              title="Devolver a bodega"
                              className="h-8 w-8 p-0 text-amber-600 hover:text-amber-700 hover:bg-amber-50 opacity-40 group-hover:opacity-100 transition-opacity duration-150"
                              aria-label={`Devolver ${it.serial_global}`}
                            >
                              <Undo2 className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => onVerHistorial(it.id_item_maestro)}
                            title="Ver historial del activo"
                            className="h-8 w-8 p-0 opacity-40 group-hover:opacity-100 transition-opacity duration-150"
                            aria-label={`Ver historial de ${it.serial_global}`}
                          >
                            <History className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>

        {/* ── Pagination ──────────────────────────────────────────────── */}
        {total > PAGE_SIZE && (
          <div className="flex items-center justify-between border-t px-4 py-3 bg-muted/10">
            <p className="text-xs text-muted-foreground">
              Página{' '}
              <span className="font-semibold text-foreground">{page}</span>
              {' '}de{' '}
              <span className="font-semibold text-foreground">{totalPages}</span>
              {' '}·{' '}
              <span className="font-semibold text-foreground tabular-nums">{total}</span> activos
            </p>
            <div className="flex items-center gap-1">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1 || isFetching}
                className="h-8 w-8 p-0"
                aria-label="Página anterior"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages || isFetching}
                className="h-8 w-8 p-0"
                aria-label="Página siguiente"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      <ResumenActivoDialog
        item={resumen}
        onOpenChange={(open) => !open && setResumen(null)}
        onVerHistorial={onVerHistorial}
      />
    </div>
  );
}
